/**
 * Pure heuristic estimator for a car's yearly running cost, driven by the
 * engine spec scraped into `parsed.json` and the current market price. The
 * coefficients are rough rules of thumb, not a real actuarial model.
 */

export interface EngineSpec {
  engine: string;
  power: string;
  productionStartYear: string;
  productionEndYear: string;
  fuelType: string;
  fuelSystem: string;
  weight: string;
  length: string;
  width: string;
  height: string;
  urbanConsumption?: string;
  extraUrbanConsumption?: string;
  combinedConsumption: string;
  emissionStandard: string;
}

export type EuroEmissionStandard = 'EURO I' | 'EURO II' | 'EURO III' | 'EURO IV' | 'EURO V' | 'EURO VI';

const KW_PER_HP = 0.7457;
const DEFAULT_FUEL_PRICE_PER_LITER = 2.8;
const REPAIR_RATE_OF_PRICE = 0.03;
const INSURANCE_RATE_OF_PRICE = 0.05;

/** Strips everything but digits, e.g. "150 hp" -> 150, "12,345 лв" -> 12345. */
export function extractDigits(text: string): number {
  return Number(text.replace(/\D/g, ''));
}

/** Parses a leading decimal number, e.g. "7.5 Liters/100 km" -> 7.5. Unlike extractDigits, preserves the decimal point. */
export function parseLeadingNumber(text: string): number {
  return Number(text.split(' ')[0]);
}

export function emissionStandardToTier(standard: string): number {
  switch (standard.toUpperCase() as EuroEmissionStandard) {
    case 'EURO I':
      return 1;
    case 'EURO II':
      return 2;
    case 'EURO III':
      return 3;
    case 'EURO IV':
      return 4;
    case 'EURO V':
      return 5;
    case 'EURO VI':
      return 6;
    default:
      return 4;
  }
}

export function vehicleAgeCoefficient(productionStartYear: number, referenceYear = new Date().getFullYear()): number {
  const age = referenceYear - productionStartYear;
  if (age <= 5) return 2.3;
  if (age <= 10) return 1.5;
  if (age <= 15) return 1.3;
  if (age <= 20) return 1;
  return 1.1;
}

export function emissionTierCoefficient(tier: number): number {
  switch (tier) {
    case 1:
      return 1.1;
    case 2:
      return 1.4;
    case 3:
      return 1.1;
    case 4:
      return 1;
    case 5:
      return 0.8;
    case 6:
      return 0.4;
    default:
      return 1;
  }
}

export function powerCoefficient(kw: number): number {
  if (kw <= 55) return 0.34;
  if (kw <= 74) return 0.54;
  if (kw <= 110) return 1.21;
  if (kw <= 150) return 1.42;
  if (kw <= 245) return 1.84;
  return 2.42;
}

export function calculateYearlyFuelLiters(kmsPerYear: number, combinedConsumptionPer100km: number): number {
  return (kmsPerYear / 100) * combinedConsumptionPer100km;
}

export function calculateYearlyFuelCost(
  kmsPerYear: number,
  engine: EngineSpec,
  pricePerLiter = DEFAULT_FUEL_PRICE_PER_LITER,
): number {
  const consumption = parseLeadingNumber(engine.combinedConsumption);
  return calculateYearlyFuelLiters(kmsPerYear, consumption) * pricePerLiter;
}

export function calculateYearlyGovernmentTax(engine: EngineSpec, referenceYear?: number): number {
  const kw = extractDigits(engine.power) * KW_PER_HP;
  const tier = emissionStandardToTier(engine.emissionStandard);
  const startYear = extractDigits(engine.productionStartYear);

  return kw * powerCoefficient(kw) * vehicleAgeCoefficient(startYear, referenceYear) * emissionTierCoefficient(tier);
}

export function calculateYearlyRepairs(currentPrice: number): number {
  return currentPrice * REPAIR_RATE_OF_PRICE;
}

export function calculateYearlyInsurance(currentPrice: number): number {
  return currentPrice * INSURANCE_RATE_OF_PRICE;
}

export interface FuelPricesPerLiter {
  petrol: number;
  diesel: number;
}

/** Picks the petrol or diesel price for an engine spec's fuel type, e.g. "Petrol (Gasoline)" or "Diesel". */
export function pickFuelPricePerLiter(fuelType: string, prices: FuelPricesPerLiter): number {
  return fuelType.toLowerCase().includes('diesel') ? prices.diesel : prices.petrol;
}

/**
 * Combined L/100 km for the engine, falling back to the mean of its urban and extra-urban
 * figures (whichever exist), or null when the spec has no usable consumption at all -
 * common for older engines, where treating it as 0 would claim the car runs on nothing.
 */
export function consumptionPer100km(engine: EngineSpec): number | null {
  const readings = [engine.combinedConsumption, engine.urbanConsumption, engine.extraUrbanConsumption].map((text) =>
    text ? parseLeadingNumber(text) : NaN,
  );
  const [combined, ...partial] = readings.map((value) => (Number.isFinite(value) && value > 0 ? value : null));
  if (combined !== null) {
    return combined;
  }
  const available = partial.filter((value): value is number => value !== null);
  return available.length ? available.reduce((sum, value) => sum + value, 0) / available.length : null;
}

export interface YearlyExpenses {
  /** Null when there's no consumption figure to work from. */
  fuelLiters: number | null;
  fuelCost: number | null;
  repairs: number;
  /** Null when the spec has no readable power figure. */
  governmentTax: number | null;
  insurance: number;
  /** Fuel + tax + insurance, counting only the parts we could estimate; repairs are shown separately. */
  total: number;
}

export function calculateYearlyExpenses(
  engine: EngineSpec,
  currentPrice: number,
  kmsPerYear: number,
  fuelPricePerLiter = DEFAULT_FUEL_PRICE_PER_LITER,
  consumption: number | null = consumptionPer100km(engine),
): YearlyExpenses {
  const fuelLiters = consumption !== null ? calculateYearlyFuelLiters(kmsPerYear, consumption) : null;
  const fuelCost = fuelLiters !== null ? fuelLiters * fuelPricePerLiter : null;
  const repairs = calculateYearlyRepairs(currentPrice);
  const governmentTax = extractDigits(engine.power) > 0 ? calculateYearlyGovernmentTax(engine) : null;
  const insurance = calculateYearlyInsurance(currentPrice);

  return {
    fuelLiters,
    fuelCost,
    repairs,
    governmentTax,
    insurance,
    total: (fuelCost ?? 0) + (governmentTax ?? 0) + insurance,
  };
}
